import { useCallback } from "react"
import { AxiosResponse } from "axios"
import { useAsync } from "hooks/core/useAsync"

const ASYNC_POLL_INTERVAL_MS = 5000
/** Same 20-minute safety cap the api/asyncJob loop applies. */
const ASYNC_MAX_WAIT_MS = 20 * 60 * 1000

const ASYNC_TIMEOUT_MESSAGE =
  "The request is still running after 20 minutes; try again later"

interface UseAsyncCapableReturn {
  /**
   * Resolves the body of an async-capable request: a 200 is returned as-is,
   * a 202 carries a job id that is polled every 5 s until the result is ready,
   * or for at most 20 minutes — after that the promise rejects.
   */
  awaitAsyncCapable: <T>(response: AxiosResponse<T | string>) => Promise<T>
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * The 200/202 half of the server's async-job contract, as a hook.
 *
 * Same loop the AI conversion modal runs inline (poll `/async-ready` every
 * 5 s, then read `/async-get`), lifted out so any async-capable call can be
 * awaited as one promise. Uses `useAsync` for the transport, so the parsed
 * result body arrives as an object exactly as it does there.
 *
 * The loop is capped at 20 minutes, the same safety cap `api/asyncJob` applies:
 * without it a job the server has quietly dropped leaves the caller polling for
 * the life of the page, with no way to tell the user anything.
 *
 * @example
 * const { awaitAsyncCapable } = useAsyncCapable()
 * const result = await awaitAsyncCapable<MyResponse>(await submit(...))
 */
export const useAsyncCapable = (): UseAsyncCapableReturn => {
  const { pollingAsync, getAsyncResult } = useAsync()

  const awaitAsyncCapable = useCallback(
    async <T>(response: AxiosResponse<T | string>): Promise<T> => {
      if (response.status === 200) {
        return response.data as T
      }
      if (response.status !== 202) {
        throw new Error(`Unexpected response status ${response.status}`)
      }

      const asyncID = String(response.data).trim()
      const started = Date.now()
      for (;;) {
        if (Date.now() - started > ASYNC_MAX_WAIT_MS) {
          throw new Error(ASYNC_TIMEOUT_MESSAGE)
        }
        await sleep(ASYNC_POLL_INTERVAL_MS)
        const ready = await pollingAsync(asyncID)
        if (ready.status === 200) {
          const result = await getAsyncResult(asyncID)
          return result.data as T
        }
        // 202 => still running; keep polling.
      }
    },
    [pollingAsync, getAsyncResult]
  )

  return { awaitAsyncCapable }
}
